/**
 * usePracticeAreas — the matter types this firm works in.
 * @file src/web/shared/hooks/usePracticeAreas.ts
 *
 * Every screen that asks for a matter type used to ask for it as free text
 * with `personal-injury` as the placeholder. The type is not a label: it
 * decides which facts a draft matter asks for, which document checklist a
 * request starts from, and which stages and agreements are on offer, and it
 * is matched by substring — so `PI` matched none of it, and `personal injury`
 * and `personal-injury` were two practice areas no report added together.
 *
 * The list comes from the firm's settings and already includes anything in
 * use, so no existing form loses its own type (see the API's `options`).
 * Until it loads, the standard eleven are returned rather than nothing: a
 * picker that renders empty and then fills is one a person clicks into
 * before it has anything to offer.
 *
 * @llm-rule WHEN: A screen offers, stores or shows a matter type
 * @llm-rule AVOID: A free-text matter type input — that is what this replaces
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '@/shared/api';
import {
  DEFAULT_PRACTICE_AREAS,
  practiceAreaLabel,
  slugifyPracticeArea,
  type PracticeArea,
} from '@shared/practice-areas';
import { fetchFirmProfile, invalidateFirmProfile } from './useFirmLanguages';

export interface FirmPracticeAreas {
  /** What to offer, in the firm's own order. */
  areas: PracticeArea[];
  loading: boolean;
  /** What to call a stored value, including one no longer offered. */
  labelFor: (value: string | null | undefined) => string;
  /**
   * The options a picker should show when something is already selected.
   * A value the list has lost — a form typed before the list existed, or an
   * area the firm has since removed — is offered anyway, because a picker
   * that cannot show its own current value silently clears it on save.
   */
  optionsFor: (value?: string | null) => PracticeArea[];
  /**
   * Add one to the firm's list and select it. Admin only; the API refuses
   * anyone else. Returns the slug that was stored, or null if it failed.
   */
  add: (label: string) => Promise<string | null>;
}

export function usePracticeAreas(): FirmPracticeAreas {
  const [areas, setAreas] = useState<PracticeArea[]>(() => [...DEFAULT_PRACTICE_AREAS]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    void fetchFirmProfile().then((p) => {
      if (!live) return;
      if (p.practiceAreas.length) setAreas(p.practiceAreas);
      setLoading(false);
    });
    return () => {
      live = false;
    };
  }, []);

  const labelFor = useCallback(
    (value: string | null | undefined) => (value ? practiceAreaLabel(value, areas) : ''),
    [areas],
  );

  const optionsFor = useCallback(
    (value?: string | null) =>
      value && !areas.some((a) => a.value === value)
        ? [...areas, { value, label: practiceAreaLabel(value, areas) }]
        : areas,
    [areas],
  );

  const add = useCallback(
    async (label: string): Promise<string | null> => {
      const value = slugifyPracticeArea(label);
      if (!value) return null;
      if (areas.some((a) => a.value === value)) return value;

      /* Written as the firm's explicit list: whatever is on offer now, plus
       * this. A firm still on the standard eleven is choosing them by adding
       * a twelfth, which is what they mean by it. */
      const next = [...areas.map((a) => a.value), value];
      const res = await api.put<{ settings?: { practiceAreas?: string[] } }>('/api/firm-settings', {
        practiceAreas: next,
      });
      invalidateFirmProfile();
      const stored = res?.settings?.practiceAreas ?? next;
      setAreas(stored.map((v) => ({ value: v, label: practiceAreaLabel(v, areas) })));
      return value;
    },
    [areas],
  );

  return { areas, loading, labelFor, optionsFor, add };
}
